import React, { useCallback, useMemo, useState } from 'react';
import { useMatch } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { SearchDialog } from 'shared/dialogs/search';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { QueryParamOptions, useQueryParam } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { targetIsInput } from 'shared/utils/target-is-input';

import { SearchContext, SearchContextProps } from './search-context';

export type SearchProviderProps = {
  children: React.ReactNode;
};

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchText, setSearchText] = useQueryParam(QueryParamOptions.Search);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const urlIsSearch = useMatch(Paths.Search);

  const openDialog = useCallback(() => setSearchOpen(true), []);

  const closeDialog = useCallback(() => setSearchOpen(false), []);

  const setValue = useCallback(
    (value: string) => {
      // Set the search value in the queryParam
      setSearchText(value);

      // Get current URL search params
      const currentSearchParams = new URLSearchParams(window.location.search);
      // Update or add the search parameter
      currentSearchParams.set(QueryParamOptions.Search, value);

      // Check if the current page is already search
      const isSearchPage = !!urlIsSearch?.pathname;

      navigate(
        replaceUrlParams(
          Paths.Search,
          {},
          Object.fromEntries(currentSearchParams),
        ),
        { replace: isSearchPage },
      );

      // close dialog, if open
      closeDialog();
    },
    [closeDialog, navigate, setSearchText, urlIsSearch?.pathname],
  );

  const value = useMemo<SearchContextProps>(
    () => ({
      openDialog,
      setValue,
      searchValue: searchText,
    }),
    [openDialog, searchText, setValue],
  );

  useModifierKeysDown('KeyK', (e) => {
    if (targetIsInput(e.target)) {
      return; // Don't open dialog
    }
    openDialog();
  });
  // useKeysDown(
  //   [
  //     ['ControlLeft', 'KeyK'],
  //     ['ControlRight', 'KeyK'],
  //     ['MetaLeft', 'KeyK'],
  //     ['MetaRight', 'KeyK'],
  //   ],
  //   (e) => {
  //     if (targetIsInput(e.target)) {
  //       return; // Don't open dialog
  //     }
  //     openDialog();
  //   },
  // );

  return (
    <SearchContext.Provider value={value}>
      {children}
      {searchOpen && (
        <SearchDialog
          open={searchOpen}
          onClose={closeDialog}
          onShowPage={setValue}
        />
      )}
    </SearchContext.Provider>
  );
};
